import { formatAddressString, useGoogleAutocomplete } from '@/hooks/use-google-autocomplete';
import { Address } from '@/schemas/sceneDataSchema';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { GenericButton } from '@/components/DynamicUI/formElements/buttons/GenericButton';
import { Stack, TextField } from '@mui/material';
import { DynamicRenderer } from '@/components/DynamicUI/DynamicRenderer';
import { RouteFn } from '@/hooks/use-flow';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';

enum SceneType {
  MANUAL_ENTRY = 'MANUAL_ENTRY',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
}

function asString(value: unknown): string {
  if (typeof value == 'string') {
    return value;
  }

  return '';
}

export function AddressFormElement({
  form,
  component,
  route,
}: {
  form: UseFormReturn;
  component: Address;
  route: RouteFn;
}) {
  const values = {
    address1: asString(component.address1.value),
    address2: asString(component.address2.value),
    city: asString(component.city.value),
    state: asString(component.state.value),
    zip: asString(component.zip.value),
  };

  const defaultValue = formatAddressString(values);
  const addressRef = useRef<HTMLInputElement | null>(null);
  const { address1, city, state, zip, clear } = useGoogleAutocomplete(addressRef, values);

  const [sceneType, setSceneType] = useState<SceneType>(SceneType.AUTOCOMPLETE);
  const autocompleteLabel = "Can't find your address?";
  const manualEntryLabel = 'Go back';
  const swapLabel = sceneType === SceneType.AUTOCOMPLETE ? autocompleteLabel : manualEntryLabel;
  const [autocompleteFieldTouched, setAutocompleteFieldTouched] = useState(false);

  const address1Key = useMemo(() => encodeFieldKey(component.address1), [component.address1]);
  const address2Key = useMemo(() => encodeFieldKey(component.address2), [component.address2]);
  const cityKey = useMemo(() => encodeFieldKey(component.city), [component.city]);
  const stateKey = useMemo(() => encodeFieldKey(component.state), [component.state]);
  const zipKey = useMemo(() => encodeFieldKey(component.zip), [component.zip]);

  useEffect(() => {
    form.setValue(address1Key, address1);
    form.setValue(address2Key, form.getValues().address2 ?? asString(component.address2.value));
    form.setValue(cityKey, city);
    form.setValue(stateKey, state);
    form.setValue(zipKey, zip);

    if (autocompleteFieldTouched) {
      form.trigger().catch(() => {});
    }
  }, [
    address1,
    address1Key,
    address2Key,
    autocompleteFieldTouched,
    city,
    cityKey,
    component,
    form,
    state,
    stateKey,
    zip,
    zipKey,
  ]);

  const autocompleteAddressHasError = [address1Key, cityKey, stateKey, zipKey].some(
    (key) => !!form.formState.errors[key]
  );
  const autocompleteAddressError =
    sceneType === SceneType.AUTOCOMPLETE && autocompleteAddressHasError
      ? `Please select an address from the list, or click the "${autocompleteLabel}" link below if you're having trouble.`
      : null;

  function changeScene() {
    if (sceneType === SceneType.AUTOCOMPLETE) {
      setSceneType(SceneType.MANUAL_ENTRY);
    } else {
      const values = form.getValues();

      if (addressRef.current) {
        const formAddress1 = asString(values[encodeFieldKey(component.address1)]);
        const formCity = asString(values[encodeFieldKey(component.city)]);
        const formState = asString(values[encodeFieldKey(component.state)]);
        const formZip = asString(values[encodeFieldKey(component.zip)]);

        if (!formAddress1 || !formCity || !formState || !formZip) {
          addressRef.current.value = '';
        } else {
          addressRef.current.value = `${formAddress1}, ${formCity}, ${formState} ${formZip}`;
        }
      }

      setSceneType(SceneType.AUTOCOMPLETE);
    }
  }

  const onAutocompleteBlur = () => {
    if (!autocompleteFieldTouched) {
      // If the user clicks the list item rather than using the keyboard to navigate to select it,
      // there's a race condition between when the input is set as touched and when the value is
      // set, which causes a jarring error flash for a very short moment.
      setTimeout(() => setAutocompleteFieldTouched(true), 50);
      // setAutocompleteFieldTouched(true);
    }

    const acValue = addressRef.current?.value;

    // if autocomplete field is empty on blur, clear the manual entry fields
    if (!acValue) {
      clear();
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        sx={{
          display: sceneType !== SceneType.AUTOCOMPLETE ? 'none' : 'block',
        }}
        type={'text'}
        label={'Address, city, state, zip'}
        variant='outlined'
        error={autocompleteAddressHasError}
        helperText={autocompleteAddressError}
        fullWidth
        role='textbox'
        defaultValue={defaultValue}
        required={sceneType === SceneType.AUTOCOMPLETE}
        inputRef={(e: HTMLInputElement | null) => {
          addressRef.current = e;
        }}
        onBlur={onAutocompleteBlur}
        onKeyDown={(ev) => ev.key === 'Enter' && ev.preventDefault()}
      />
      {sceneType === SceneType.AUTOCOMPLETE && (
        <DynamicRenderer
          form={form}
          components={[component.address2]}
          route={route}
        />
      )}

      {sceneType === SceneType.MANUAL_ENTRY && (
        <DynamicRenderer
          form={form}
          components={[component.address1, component.address2, component.city, component.state, component.zip]}
          route={route}
        />
      )}
      <GenericButton
        type='button'
        label={swapLabel}
        id={swapLabel}
        variant='text'
        size='small'
        onClick={changeScene}
      />
    </Stack>
  );
}
